import type { NextStep } from '@repo/common';

import { Step } from '@repo/common';

import { DocumentCapture } from './document';
import { EnrolmentPendingReview } from './enrolment-pending-review';
import { FaceCapture } from './face';
import { MobileRedirect } from './mobile-redirect';
import { MobileRedirectErrorBoundary } from './mobile-redirect/error-boundary';
import { MobileRedirectCanceled } from './mobile-redirect-canceled';
import { MultiRangeLiveness } from './multirange';
import { NfcCapture } from './nfc';
import { PalmCapture } from './palm';
import { SmileLiveness } from './smile';
import { VerificationFailed } from './verification-failed';
import { VerificationSuccess } from './verification-success';

import { Loading } from '@/components/Loading/Loading';
import { SystemError } from '@/errors/system-error';
import { useContinueOnThisDevice } from '@/hooks/use-continue-on-this-device';
import { useHandleAction } from '@/hooks/use-handle-action';
import { useAppContext } from '@/providers/app-provider/context';
import { selectStep } from '@/providers/app-provider/selectors';
import { Introduction } from '@/steps/introduction';

/**
 * Server-driven component names → React. Same registry for every workflow id.
 */
const COMPONENT_REGISTRY: Record<string, React.FC> = {
  DocumentCapture: () => <DocumentCapture />,
  Introduction: () => <Introduction />,
  LivenessCheck: () => <FaceCapture />,
  Loading: () => <Loading />,
  Result: () => <VerificationSuccess />,
  EnrolmentPendingReview: () => <EnrolmentPendingReview />,
  MultiRangeLiveness: () => <MultiRangeLiveness />,
  NfcCapture: () => <NfcCapture />,
  PalmCapture: () => <PalmCapture />,
  SelfieCapture: () => <FaceCapture />,
  SmileLiveness: () => <SmileLiveness />,
  VerificationFailed: () => <VerificationFailed />,
};

const MobileRedirectWithBoundary = () => {
  const { continueOnThisDevice } = useContinueOnThisDevice();

  const {
    state: { customerData },
  } = useAppContext();

  const canContinueOnThisDevice = customerData?.mobileRedirectSkippable !== false;

  return (
    <MobileRedirectErrorBoundary
      onContinueOnThisDeviceClick={
        canContinueOnThisDevice
          ? () => {
              void continueOnThisDevice();
            }
          : undefined
      }
    >
      <MobileRedirect />
    </MobileRedirectErrorBoundary>
  );
};

function renderByStepValue(step: NextStep) {
  switch (step.value) {
    case Step.INTRO:
      return <Introduction />;

    case Step.MOBILE_REDIRECT:
      return <MobileRedirectWithBoundary />;

    case Step.MOBILE_REDIRECT_CANCELED:
      return <MobileRedirectCanceled />;

    case Step.DOCUMENT_FRONT:
    case Step.DOCUMENT_BACK:
    case Step.DOCUMENT_OCR_FRONT:
    case Step.DOCUMENT_OCR_BACK:
    case Step.AGE_DOCUMENT_FRONT:
      return <DocumentCapture />;

    case Step.FACE:
    case Step.AGE_FACE:
      return <FaceCapture />;

    case Step.SUCCESS:
      return <VerificationSuccess />;

    case Step.FAIL:
      return <VerificationFailed />;

    default:
      return null;
  }
}

export const Steps = () => {
  const { state } = useAppContext();
  const step = selectStep(state);

  useHandleAction(step);

  if (!step) {
    console.warn('Steps null', step);

    return null;
  }

  if (step.component === 'MobileRedirect' || step.value === Step.MOBILE_REDIRECT) {
    return <MobileRedirectWithBoundary />;
  }

  if (step.component && COMPONENT_REGISTRY[step.component]) {
    const Cmp = COMPONENT_REGISTRY[step.component];

    return <Cmp />;
  }

  const legacy = renderByStepValue(step);

  if (legacy) {
    return legacy;
  }

  throw new SystemError(undefined, undefined, `Unknown step: ${step.value}`);
};
